import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { useQuery } from 'react-query';
import { getClientsIncome } from '../../../services/Report/getClientsIncome';
import { useAppContext } from '../../../context/AppContext';
import { useOptionsSelect } from '../../../hooks/useOptionsSelect';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Link } from 'react-router-dom';
import Panel from '../../../components/panel/panel';
import { getOneEnterprise } from '../../../services/Enterprise/getOneEnterprise';
import axios from 'axios';

export const ClientsIR = () => {
  const [loading, setLoading] = useState(false);
  const toastIdRef = useRef(null);
  const { token } = useAppContext();

  // Filtros
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [page] = useState(1);

  // Busca empreendimentos e clientes igual ao Debtor
  const { client, enterprise, isLoadingClients, isLoadingEnterprises } = useOptionsSelect();

  // Atualiza client options conforme empreendimento selecionado
  const filteredClients = useMemo(() => {
    if (!selectedEnterprise) return [];
    return client.filter(c => c.enterprise_id === selectedEnterprise || !c.enterprise_id);
  }, [client, selectedEnterprise]);

  // Filtros obrigatórios: enterprise_id e year
  const filters = useMemo(() => {
    const filter = {};
    if (selectedEnterprise) {
      filter.enterprise_id = selectedEnterprise;
    }
    if (selectedYear) {
      filter.year = selectedYear;
    }
    if (selectedClient) {
      filter.client_id = selectedClient;
    }
    return filter;
  }, [selectedEnterprise, selectedYear, selectedClient]);

  const {
    data: mockData,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useQuery(
    ['clients-income', filters, page],
    () => getClientsIncome(filters, page),
    {
      staleTime: 1000 * 60 * 10,
      retry: 1,
      enabled: false,
      onSuccess: (data) => {
        // Atualiza o toast de busca para sucesso ou aviso
        if (toastIdRef.current) {
          if (data?.data && data.data.length > 0) {
            toast.update(toastIdRef.current, {
              render: "Dados encontrados, gerando relatórios...",
              type: "info",
              isLoading: false,
              autoClose: 1500,
            });
          } else {
            toast.update(toastIdRef.current, {
              render: "Nenhum cliente encontrado para gerar relatórios.",
              type: "warning",
              isLoading: false,
              autoClose: 3000,
            });
          }
        }
        if (data?.data && data.data.length > 0) {
          handleGenerateReports(data);
        }
      },
      onError: () => {
        if (toastIdRef.current) {
          toast.update(toastIdRef.current, {
            render: "Erro ao buscar dados.",
            type: "error",
            isLoading: false,
            autoClose: 4000,
          });
        }
      }
    }
  );

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, []);

  const generatePDF = (clientData, enterprise, year, logoBase64) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 20;
    let y = 20;

    if (logoBase64) {
      try {
        const img = new window.Image();
        img.src = logoBase64;
        let imgWidth = 40;
        let imgHeight = 40;
        img.onload = function () {
          const maxWidth = 50;
          const maxHeight = 50;
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = height * (maxWidth / width);
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = width * (maxHeight / height);
            height = maxHeight;
          }
          const x = (210 - width) / 2;
          doc.addImage(logoBase64, 'JPEG', x, y, width, height);
        };
        doc.addImage(logoBase64, 'JPEG', 85, y, imgWidth, imgHeight);
        y += 50;
      } catch (e) {}
    }

    if (enterprise?.name) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text(enterprise.name, 105, y, { align: 'center' });
      y += 8;
    }

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 40, 80);
    doc.text(`Informe de Rendimentos - IRPF ${clientData.base_year || year}`, 105, y, { align: 'center' });
    y += 12;

    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.5);
    doc.line(margin, y, 190, y);
    y += 6;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0);

    const infoRows = [
      ['CPF/CNPJ:', clientData.tax_id || clientData.document || '-'],
      ['Contrato:', clientData.contract || '-'],
      ['Empreendimento:', clientData.enterprise || '-'],
      ['Bloco/Unidade:', clientData.block_unit || '-'],
      ['Mutuários:', clientData.borrowers || '-'],
      ['Fonte recebedora:', clientData.receiver_source || '-'],
      [`Saldo devedor em 31/12/${clientData.base_year || year}:`, clientData.outstanding_balance || '-'],
      [`Total pago - Prestações (${clientData.base_year || year}):`, clientData.total_paid_installments || '-'],
      ['Total pago - Amortizações:', clientData.total_paid_extra_amortizations || '-'],
      ['Total pago no período:', clientData.total_paid_period || '-'],
    ];

    infoRows.forEach(([label, value], idx) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), margin + 65, y);
      y += 8;
    });

    y += 2;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, y, 190, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Este documento foi gerado automaticamente pelo sistema.', margin, y);

    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Data de emissão: ${new Date().toLocaleDateString('pt-BR')}`, margin, y);

    doc.setFontSize(9);
    doc.setTextColor(180, 180, 180);
    doc.text('Relatório gerado por Lote Mobile', 105, 287, { align: 'center' });

    return doc.output('blob');
  };

  // Agora recebe os dados como parâmetro para ser chamado pelo onSuccess da query
  const handleGenerateReports = async (dataToProcess) => {
    const data = dataToProcess || mockData;
    if (!data?.data || data.data.length === 0) {
      toast.update(toastIdRef.current, {
        render: 'Nenhum cliente encontrado para gerar relatórios.',
        type: 'warning',
        isLoading: false,
        autoClose: 3000
      });
      setLoading(false);
      return;
    }

    // Monta o base64 da logo (mockData.enterprise.img já é base64 puro, precisa prefixar)
    let logoBase64 = null;
    if (data.enterprise?.img) {
      // Se já tem prefixo, mantém, senão adiciona
      logoBase64 = data.enterprise.img.startsWith('data:image')
        ? data.enterprise.img
        : `data:image/jpeg;base64,${data.enterprise.img}`;
    }

    setLoading(true);
    toastIdRef.current = toast.loading(`Gerando ${data.data.length} relatório(s)...`);

    try {
      const zip = new JSZip();
      const reportsFolder = zip.folder(`Informes_Rendimentos_${data.year}`);

      data.data.forEach((client) => {
        const documentValue = client.tax_id || client.document || '';
        const pdfBlob = generatePDF(client, data.enterprise, data.year, logoBase64);
        const sanitizedName = (client.borrowers || 'Cliente').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
        const sanitizedDocument = documentValue.replace(/[^0-9]/g, '');
        const fileName = `IR_${data.year}_${sanitizedName}_${client.contract}_${sanitizedDocument}.pdf`;
        reportsFolder.file(fileName, pdfBlob);
      });

      const zipContent = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `Informes_Rendimentos_${data.enterprise.name}_${data.year}.zip`;
      saveAs(zipContent, zipFileName);

      toast.update(toastIdRef.current, {
        render: `${data.data.length} relatório(s) gerado(s) com sucesso!`,
        type: 'success',
        isLoading: false,
        autoClose: 3000
      });

    } catch (error) {
      console.error('Erro ao gerar relatórios:', error);
      toast.update(toastIdRef.current, {
        render: 'Erro ao gerar os relatórios. Tente novamente.',
        type: 'error',
        isLoading: false,
        autoClose: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const items = [
    {
      template: () => (
        <Link to={"/reports/clientsIR"} className="p-breadcrumb-link">
          Relatório de Informe de Rendimentos
        </Link>
      ),
    },
  ];

  const home = {
    template: () => (
      <Link to={"#"} className="p-breadcrumb-link">
        <i className="fa fa-home"></i>
      </Link>
    ),
  };

  return (
    <>
      {/* Filtros */}
      <div className="row mb-3 justify-content-between">
        <div className="col-md-6 d-flex align-items-center">
          <BreadCrumb
            model={items}
            home={home}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "16px",
            }}
          />
        </div>
      </div>
      <Panel title="Buscar Informe de Rendimentos" className="p-4 mb-4">
        <div className="row align-items-end py-2">
          <div className="col-md-4">
            <label className="form-label fw-semibold">Empreendimento</label>
            <Dropdown
              value={selectedEnterprise}
              onChange={e => {
                setSelectedEnterprise(e.value);
              }}
              options={enterprise}
              optionLabel="label"
              placeholder={isLoadingEnterprises ? "Carregando..." : "Selecione"}
              className="w-100"
              filter
              showClear
              disabled={isLoadingEnterprises}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-semibold">Cliente</label>
            <Dropdown
              value={selectedClient}
              onChange={e => setSelectedClient(e.value)}
              options={filteredClients}
              optionLabel="label"
              placeholder={isLoadingClients ? "Carregando..." : "Todos"}
              className="w-100"
              filter
              showClear
              disabled={!selectedEnterprise || isLoadingClients}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-semibold">Ano</label>
            <Dropdown
              value={selectedYear}
              onChange={e => setSelectedYear(e.value)}
              options={yearOptions}
              placeholder="Ano"
              className="w-100"
              showClear
              optionLabel={v => v}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <Button
              label={isFetching ? "Buscando..." : "Buscar"}
              icon={isFetching ? "pi pi-spin pi-spinner" : "pi pi-search"}
              className="p-button-primary w-100"
              onClick={() => {
                if (!selectedEnterprise || !selectedYear) {
                  toast.warn("Selecione o empreendimento e o ano.");
                  return;
                }
                // Se já existe um toast de busca, fecha antes de abrir outro
                if (toastIdRef.current) {
                  toast.dismiss(toastIdRef.current);
                }
                toastIdRef.current = toast.loading("Buscando dados, aguarde...", { toastId: "busca-clientes-ir" });
                refetch();
              }}
              disabled={isFetching || !selectedEnterprise || !selectedYear}
            />
          </div>
        </div>
      </Panel>
    </>
  );
};

/*
Observação: 
No PDF (jsPDF), não é possível usar uma URL de imagem diretamente devido a restrições de CORS e limitações da biblioteca.
O correto é sempre converter a imagem para base64/DataURL antes de usar no PDF.
Para exibir a logo na tela (em HTML), pode-se usar a URL normalmente em uma tag <img>, mas para PDF não há solução universal sem CORS.
*/